/* Router factory + openAddress helper (MULTI_WINDOW_PLAN W1).
   Web = real paths (createBrowserRouter); desktop = `#` + path
   (createHashRouter — file:// has no server to resolve paths). The whole
   app stays ONE route element (the shell); routing feeds app state. */

import type { ReactElement } from 'react';
import { createBrowserRouter, createHashRouter } from 'react-router';
import { isWeb } from '../lib/platform';

type AppRouter = ReturnType<typeof createBrowserRouter>;
let current: AppRouter | null = null;

export function createAppRouter(shell: ReactElement): AppRouter {
  const routes = [{ path: '*', element: shell }];
  current = isWeb ? createBrowserRouter(routes) : createHashRouter(routes);
  return current;
}

interface WindowOpenApi {
  api?: { windowOpen?: (address: string) => unknown };
}

/** Open an address. `newWindow`: web = new tab; desktop = second window via
 *  `window.api.windowOpen` when W3's IPC is present, else in-window. */
export function openAddress(address: string, opts: { newWindow?: boolean } = {}): void {
  if (opts.newWindow) {
    if (isWeb) {
      window.open(address, '_blank');
      return;
    }
    const open = (window as unknown as WindowOpenApi).api?.windowOpen;
    if (typeof open === 'function') {
      void open(address);
      return;
    }
  }
  if (current) void current.navigate(address);
  else window.location.assign(isWeb ? address : `#${address}`);
}

/* Desktop sign-in handoff: remember the address this window was opened for
   (per window = sessionStorage) so it survives the browser round trip. */
const STASH_KEY = 'sf-route-stash';

export function stashAddress(address: string): void {
  try {
    if (address && address !== '/') sessionStorage.setItem(STASH_KEY, address);
  } catch {
    /* storage unavailable — the hash itself still carries the address */
  }
}

export function takeStashedAddress(): string | null {
  try {
    const a = sessionStorage.getItem(STASH_KEY);
    if (a) sessionStorage.removeItem(STASH_KEY);
    return a;
  } catch {
    return null;
  }
}
